import { useMemo } from 'react';

import { useField } from '@adflow/frontend/util-common';

import type { FormControlProps } from '../FormControl';
import { FormControl } from '../FormControl';

import type { SelectorSelectProps } from './SelectorSelect';
import { SelectorSelect } from './SelectorSelect';

export function convertItemsToLookUp<Item extends string>(
	items: readonly Item[],
	formatFunc: (item: Item) => string
): {
	keys: string[];
	lookUp: Record<string, Item>;
	reverseLookUp: Record<Item, string>;
} {
	// eslint-disable-next-line unicorn/no-array-reduce
	return items.reduce(
		(table, item) => {
			return {
				keys: [...table.keys, formatFunc(item)],
				lookUp: {
					...table.lookUp,
					[formatFunc(item)]: item,
				},
				reverseLookUp: {
					...table.reverseLookUp,
					[item]: formatFunc(item),
				},
			};
		},
		{
			keys: [] as string[],
			lookUp: {},
			reverseLookUp: {} as Record<Item, string>,
		}
	);
}
export interface SelectorSelectWithLookupAndControlProps
	extends
		Omit<SelectorSelectProps, 'value' | 'onChange' | 'items'>,
		FormControlProps {
	label?: string;
	name: string;
	infoText?: string;
	// Record key = display value, Record value = form value
	// TODO allow generics for the lookup
	lookup: Record<string, string>;
	filterList?: (item: string) => boolean;
	sortAlphabetically?: boolean;
}

export function SelectorSelectWithLookupAndControl(
	props: SelectorSelectWithLookupAndControlProps
) {
	const {
		label,
		defaultValue,
		lookup,
		outerStyle,
		infoText,
		sortAlphabetically = true,
	} = props;

	const reverseLookup = useMemo(
		() =>
			Object.fromEntries(Object.keys(lookup).map((key) => [lookup[key], key])),
		[lookup]
	);

	const {
		field: { onChange, value: formValue, ref },
		fieldState: { invalid: error },
		errorMessage,
	} = useField({
		name: props.name,
		label,
		defaultValue: defaultValue ? lookup[defaultValue] : defaultValue,
	});

	return (
		<FormControl
			error={error}
			helperText={errorMessage}
			label={label}
			labelStyle={props.labelStyle}
			outerStyle={outerStyle}
			infoText={infoText}
		>
			<SelectorSelect
				inputRef={ref}
				value={
					formValue && reverseLookup?.[formValue]
						? reverseLookup[formValue]
						: ''
				}
				onChange={(displayValue) => {
					onChange(
						displayValue && lookup?.[displayValue] ? lookup[displayValue] : ''
					);
				}}
				items={Object.keys(lookup)
					.filter((key) => !props.filterList || props.filterList(key))
					.sort((a, b) => (sortAlphabetically ? a.localeCompare(b) : 0))}
				dropdownMaxHeight="auto"
				{...props}
				error={error}
			/>
		</FormControl>
	);
}
